import { UpsellBillingTrigger } from "@redotech/congo-enums/upsell-billing-trigger";
import { Currency } from "@redotech/money/currencies";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { AddressInfo } from "@redotech/redo-customer-portal-shared/hooks/useAddress/use-address";
import { CustomerPortalAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { ReturnItemParams } from "@redotech/redo-customer-portal-shared/util";
import { useTranslation } from "@redotech/redo-i18n";
import { SerializedReturnSummaryTotals } from "@redotech/redo-return-core-common/returns/return-totals/types";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMemo } from "react";
import { ItemDetail } from "../../shared-components/item-detail";
import { Addresses } from "../../submission/components/addresses";
import {
  RefundMethodSummary,
  RefundMethodSummaryProps,
} from "../components/refund-method-summary";
import { ReturnItems } from "../components/return-items";
import { SummaryCardConfirmation } from "../components/summary-card-confirmation";

export interface ConfirmationSummarySectionProps {
  settings: CustomerPortalAppSettings | undefined;

  products: ReturnItemParams[] | undefined;
  currency: Currency;
  returnItemsHeader: string;
  newItems: ReturnItemParams[] | undefined;
  newItemsHeader: string;

  returnTotals: SerializedReturnSummaryTotals | undefined;
  summaryFeesHeader: string;
  summaryFeesOrderHeader: string;
  labelDeducted: boolean;
  isLabelCovered?: boolean;
  upsellBillingTrigger?: UpsellBillingTrigger | null;
  isConvenienceFee: boolean;
  /** Not carried by RAC totals — read from the return. */
  chargesExchangeShippingViaStripe: boolean;
  exchangeShippingName: string;
  /** Warranty "Hide warranty item costs" setting: hides the summary totals
   * (unless return shipping applies) and all per-item prices. */
  hideItemCosts?: boolean;
  isWarranty?: boolean;
  originalOrderShippingAmount?: number;
  outstandingBalanceManuallyWaived?: boolean;

  showAddresses: boolean;
  newOrderAddress: AddressInfo | undefined;
  returnAddress: AddressInfo | undefined;
  showEditAddressButton: boolean;
  onEditAddressClick: () => void;
  /** Absent when the refund isn't going to a wallet Redo pays directly. */
  refundMethod?: RefundMethodSummaryProps;
  modalless?: boolean;
}

export function ConfirmationSummarySection({
  settings,
  products,
  currency,
  returnItemsHeader,
  newItems,
  newItemsHeader,
  returnTotals,
  summaryFeesHeader,
  summaryFeesOrderHeader,
  labelDeducted,
  isLabelCovered = false,
  upsellBillingTrigger,
  isConvenienceFee,
  chargesExchangeShippingViaStripe,
  exchangeShippingName,
  hideItemCosts = false,
  isWarranty = false,
  originalOrderShippingAmount = 0,
  outstandingBalanceManuallyWaived,
  showAddresses,
  newOrderAddress,
  returnAddress,
  showEditAddressButton,
  onEditAddressClick,
  refundMethod,
  modalless = false,
}: ConfirmationSummarySectionProps) {
  const { t } = useTranslation();
  const screen = useScreenType();
  const shouldShowDesktopView = screen === ScreenType.DESKTOP;

  const SummaryCard = useMemo(
    () =>
      returnTotals ? (
        <Flex flexDirection="column" pb="4xl">
          <SummaryCardConfirmation
            chargesExchangeShippingViaStripe={chargesExchangeShippingViaStripe}
            currency={currency}
            exchangeShippingName={exchangeShippingName}
            feesHeader={summaryFeesHeader}
            feesOrderHeader={summaryFeesOrderHeader}
            hideItemCosts={hideItemCosts}
            isConvenienceFee={isConvenienceFee}
            isLabelCovered={isLabelCovered}
            isWarranty={isWarranty}
            labelDeducted={labelDeducted}
            newItems={newItems}
            originalOrderShippingAmount={originalOrderShippingAmount}
            outstandingBalanceManuallyWaived={outstandingBalanceManuallyWaived}
            products={products}
            settings={settings}
            totals={returnTotals}
            upsellBillingTrigger={upsellBillingTrigger}
          />
        </Flex>
      ) : null,
    [
      labelDeducted,
      isLabelCovered,
      returnTotals,
      currency,
      summaryFeesHeader,
      summaryFeesOrderHeader,
      newItems,
      products,
      settings,
      upsellBillingTrigger,
      isConvenienceFee,
      chargesExchangeShippingViaStripe,
      exchangeShippingName,
      hideItemCosts,
      isWarranty,
      originalOrderShippingAmount,
      outstandingBalanceManuallyWaived,
    ],
  );

  const AddressesComponent = useMemo(() => {
    const addresses = [];
    if (returnAddress) {
      addresses.push(returnAddress);
    }
    if (newOrderAddress) {
      addresses.push(
        showEditAddressButton
          ? { ...newOrderAddress, edit: onEditAddressClick }
          : newOrderAddress,
      );
    }
    return showAddresses ? (
      <Flex data-ui="addresses" flexDirection="column" gap="lg" pt="md">
        <Text as="h2" fontWeight="bold">
          {t("Shipping information")}
        </Text>
        <Addresses addresses={addresses} skipComparison />
      </Flex>
    ) : null;
  }, [
    showAddresses,
    showEditAddressButton,
    newOrderAddress,
    returnAddress,
    onEditAddressClick,
    t,
  ]);

  return (
    <Flex
      data-ui="summary-section"
      flexDirection="column"
      gap="2xl"
      px={shouldShowDesktopView ? "3xl" : "none"}
    >
      {shouldShowDesktopView && SummaryCard}
      <Flex flexDirection="column" gap="3xl">
        <ReturnItems
          currency={currency}
          header={returnItemsHeader}
          hidePrices={hideItemCosts}
          products={products}
          settings={settings}
        />
        <Divider />
        {newItems && newItems?.length > 0 && (
          <Flex data-ui="exchange-items" flexDirection="column" gap="lg">
            <Flex flexDirection="column" gap="xl">
              <Text as="h2" fontSize="lg" fontWeight="semibold">
                {newItemsHeader}
              </Text>
              {newItems?.map((item, i) => (
                <ItemDetail
                  hidePrice={hideItemCosts}
                  item={item}
                  key={item.title + i}
                  settings={settings}
                />
              ))}
            </Flex>
            <Divider />
          </Flex>
        )}
      </Flex>
      {AddressesComponent}
      {!!refundMethod && (
        <Flex flexDirection="column" gap="lg" pt="md">
          <RefundMethodSummary {...refundMethod} />
        </Flex>
      )}
      {!shouldShowDesktopView && SummaryCard}
    </Flex>
  );
}
